<!--
 * @Description: 首页线索投放情况
 * @Author: mdb
 * @Date: 2023-04-11 13:41:37
 * @LastEditTime: 2023-04-18 10:18:39
 * @LastEditors: mdb
-->
<template>
  <div class="home-echarts-wrap" :style="{ width, height }">
    <div class="home-echarts-header">
      <h3 class="home-echarts-header__title">{{ title }}</h3>
      <div class="home-echarts-header__controls">
        <CosRangePickerMonth
          style="width: 220px"
          v-model:startTime="queryForm.countDate"
          :allowClear="props.allowClear"
          @change="getData"
        />
      </div>
    </div>
    <!-- 延迟显示 loading 效果。当 spinning 状态在 delay 时间内结束，则不显示 loading 状态。 -->
    <a-spin :spinning="spinning" :delay="200">
      <!-- echarts 图表 -->
      <div ref="chartRef" class="home-echarts-content" v-show="showECharts"></div>
      <dc-layout-empty type="nothing" v-if="!showECharts" />
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onBeforeMount, inject } from 'vue'
import { useECharts } from '@/hooks/useECharts'
import { getCurrentMonthDateRange } from '@/utils/timeFormat'
import CosRangePickerMonth from '@/components/CosRangePickerMonth/index.vue'
import { zbuCluePutCondition } from '@/api/marketing/index'
import type { Ref, PropType } from 'vue'
interface ItemType {
  pushCount: number
  dateTime: string
}
enum Subdomain {
  Fbu = 'fbu',
  Lbu = 'lbu'
}
const subdomain = inject<Subdomain>('subdomain')

const props = defineProps({
  title: {
    type: String as PropType<string>,
    required: true
  },
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '437px'
  },
  allowClear: {
    type: Boolean as PropType<boolean>,
    default: false
  }
})

const spinning = ref(false)
const showECharts = ref(false)
const { currentDayOfMonth } = getCurrentMonthDateRange()
const queryForm = reactive({
  countDate: currentDayOfMonth
})
const getData = () => {
  spinning.value = true
  const params = { countDate: queryForm.countDate.split(' ')[0] }
  zbuCluePutCondition(params)
    .then((res) => {
      const data = res.data as unknown as ItemType[]
      if (data.length === 0) {
        showECharts.value = false
        initChart([])
      } else {
        showECharts.value = true
        initChart(data)
      }
    })
    .finally(() => {
      spinning.value = false
    })
}

const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>)
const initChart = (data: ItemType[]) => {
  setOptions({
    tooltip: {
      trigger: 'axis'
    },
    toolbox: {
      feature: {
        magicType: {
          show: false,
          type: ['line', 'bar']
        }
      }
    },
    grid: {
      left: '80',
      right: '50'
    },
    calculable: false,
    xAxis: [
      {
        type: 'category',
        data: data.map((item) => item.dateTime),
        axisLabel: {
          // 倾斜的程度
          formatter: function (value: any) {
            if (value.length > 7) {
              return `${value.slice(0, 7)}...`
            }
            return value
          }
        }
      }
    ],
    yAxis: [
      {
        type: 'value',
        axisLine: {
          show: false,
          lineStyle: {
            type: 'dashed'
          }
        },
        axisLabel: {
          // 倾斜的程度
          margin: 2,
          formatter: function (value: any) {
            if (value >= 10000 && value < 100000000) {
              value = value / 10000 + '万'
            } else if (value >= 100000000 && value < 1000000000000) {
              value = value / 100000000 + '亿'
            } else if (value >= 1000000000000) {
              value = value / 1000000000000 + '兆'
            }
            return value
          }
        },
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        }
      }
    ],
    series: [
      {
        name: '线索投放',
        type: 'line',
        itemStyle: {
          color: subdomain === Subdomain.Lbu ? '#FFB648' : '#3F97FF'
        },
        data: data.map((item) => item.pushCount)
      }
    ]
  })
}
onBeforeMount(getData)
</script>
<script lang="ts">
export default { name: 'LeadDeliveryEchart' }
</script>

<style scoped></style>
